import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ArrowSquareOut, Copy, Trash, Power } from "@phosphor-icons/react";

const FE_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Dashboard() {
    const { t } = useI18n();
    const { user } = useAuth();
    const [bracelets, setBracelets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claimCode, setClaimCode] = useState("");
    const [claimLabel, setClaimLabel] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null); // code of bracelet being edited

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bracelets/mine");
            setBracelets(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onClaim = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/bracelets/claim", { code: claimCode, label: claimLabel });
            setClaimCode("");
            setClaimLabel("");
            toast.success("OK");
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const toggleActive = async (b) => {
        try {
            await api.put(`/bracelets/${b.code}`, { is_active: !b.is_active });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async (b) => {
        if (!confirm(`Delete ${b.code}?`)) return;
        try {
            await api.delete(`/bracelets/${b.code}`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const copyLink = (code) => {
        const url = `${window.location.origin}/b/${code}`;
        navigator.clipboard.writeText(url);
        toast.success("Copied!");
    };

    return (
        <div className="bg-grain min-h-screen">
            <Navbar />
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="dashboard">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="font-mono tracking-widest text-xs text-brand-primary">/ {user?.email}</div>
                        <h1 className="font-display text-4xl md:text-5xl mt-2">{t.dashboard.title}</h1>
                        <p className="text-brand-text/70 mt-2">{t.dashboard.sub}</p>
                    </div>
                </div>

                {/* Claim / Create */}
                <form onSubmit={onClaim} className="mt-8 rounded-3xl border border-brand-border bg-white p-7" data-testid="claim-form">
                    <div className="font-mono tracking-widest text-xs text-brand-textMuted uppercase">{t.dashboard.claim}</div>
                    <p className="text-sm text-brand-text/70 mt-1">{t.dashboard.claimDesc}</p>
                    <div className="mt-4 grid md:grid-cols-12 gap-3">
                        <input
                            value={claimCode}
                            onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
                            placeholder={t.dashboard.claimCode}
                            className="md:col-span-4 rounded-xl border border-brand-border bg-brand-bg px-4 py-3 text-sm font-mono tracking-widest focus:outline-none focus:border-brand-text"
                            data-testid="claim-code"
                        />
                        <input
                            value={claimLabel}
                            onChange={(e) => setClaimLabel(e.target.value)}
                            placeholder={t.dashboard.claimLabel}
                            className="md:col-span-4 rounded-xl border border-brand-border bg-brand-bg px-4 py-3 text-sm focus:outline-none focus:border-brand-text"
                            data-testid="claim-label"
                        />
                        <button type="submit" disabled={busy || !claimCode} className="md:col-span-4 rounded-full btn-primary px-4 py-3 font-semibold disabled:opacity-60" data-testid="claim-submit">
                            {t.dashboard.claimCta}
                        </button>
                    </div>
                </form>

                {/* Bracelets list */}
                <div className="mt-10">
                    {loading ? (
                        <div className="text-brand-textMuted font-mono text-xs">LOADING…</div>
                    ) : bracelets.length === 0 ? (
                        <div className="text-brand-textMuted text-center py-12 rounded-3xl border border-dashed border-brand-border bg-white" data-testid="dashboard-empty">{t.dashboard.empty}</div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-5">
                            {bracelets.map((b) => (
                                <BraceletCard key={b.code} b={b} onToggle={toggleActive} onDelete={remove} onCopy={copyLink} t={t} onEdit={() => setEditing(b.code)} isEditing={editing === b.code} onClose={() => setEditing(null)} reload={load} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}

function BraceletCard({ b, onToggle, onDelete, onCopy, t, onEdit, isEditing, onClose, reload }) {
    return (
        <div className={`rounded-3xl border bg-white p-6 ${b.is_active ? "border-brand-border" : "border-brand-border bg-brand-bg"}`} data-testid={`bracelet-card-${b.code}`}>
            <div className="flex items-start justify-between">
                <div>
                    <div className="font-mono text-xs tracking-widest text-brand-textMuted">{b.label || "Bracelet"}</div>
                    <div className="font-display text-3xl mt-1">{b.code}</div>
                </div>
                <span className={`text-xs font-mono tracking-widest px-2.5 py-1 rounded-full ${b.is_active ? "bg-brand-success/15 text-brand-success" : "bg-brand-textMuted/15 text-brand-textMuted"}`}>
                    {b.is_active ? t.dashboard.active : t.dashboard.inactive}
                </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <Link to={`/b/${b.code}`} target="_blank" className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-brand-secondary text-white font-mono tracking-widest" data-testid={`view-${b.code}`}>
                    <ArrowSquareOut size={14} weight="bold" /> {t.dashboard.view}
                </Link>
                <button onClick={() => onCopy(b.code)} className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-brand-border font-mono tracking-widest hover:border-brand-text">
                    <Copy size={14} weight="bold" /> {t.dashboard.qrLink}
                </button>
                <button onClick={() => onToggle(b)} className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-brand-border font-mono tracking-widest hover:border-brand-text" data-testid={`toggle-${b.code}`}>
                    <Power size={14} weight="bold" /> {b.is_active ? t.dashboard.deactivate : t.dashboard.activate}
                </button>
                <button onClick={() => onDelete(b)} className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-brand-border text-brand-critical font-mono tracking-widest hover:border-brand-critical">
                    <Trash size={14} weight="bold" /> {t.dashboard.delete}
                </button>
            </div>
            <button onClick={isEditing ? onClose : onEdit} className="mt-4 w-full py-3 rounded-full btn-primary font-semibold text-sm" data-testid={`edit-${b.code}`}>
                {isEditing ? "Close" : t.dashboard.edit}
            </button>
            {isEditing && <ProfileEditor b={b} t={t} reload={reload} onClose={onClose} />}
        </div>
    );
}

function ProfileEditor({ b, t, reload, onClose }) {
    const [p, setP] = useState({ ...b.profile });
    const [busy, setBusy] = useState(false);
    const update = (k) => (e) => setP((prev) => ({ ...prev, [k]: e.target.value }));
    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/bracelets/${b.code}`, { profile: p });
            toast.success("Saved");
            await reload();
            onClose();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="mt-5 pt-5 border-t border-brand-border space-y-3" data-testid={`editor-${b.code}`}>
            <EditorField k="full_name" label={t.dashboard.profile.fullName} p={p} update={update} />
            <div className="grid grid-cols-2 gap-3">
                <EditorField k="date_of_birth" label={t.dashboard.profile.dob} type="date" p={p} update={update} />
                <EditorField k="blood_type" label={t.dashboard.profile.bloodType} p={p} update={update} />
            </div>
            <EditorField k="allergies" label={t.dashboard.profile.allergies} textarea p={p} update={update} />
            <EditorField k="medical_conditions" label={t.dashboard.profile.conditions} textarea p={p} update={update} />
            <EditorField k="medications" label={t.dashboard.profile.medications} textarea p={p} update={update} />
            <EditorField k="address" label={t.dashboard.profile.address} p={p} update={update} />
            <EditorField k="notes" label={t.dashboard.profile.notes} textarea p={p} update={update} />
            <div className="grid grid-cols-2 gap-3">
                <EditorField k="emergency_contact_1_name" label={t.dashboard.profile.ec1Name} p={p} update={update} />
                <EditorField k="emergency_contact_1_phone" label={t.dashboard.profile.ec1Phone} type="tel" p={p} update={update} />
                <EditorField k="emergency_contact_2_name" label={t.dashboard.profile.ec2Name} p={p} update={update} />
                <EditorField k="emergency_contact_2_phone" label={t.dashboard.profile.ec2Phone} type="tel" p={p} update={update} />
            </div>
            <div className="pt-3 mt-3 border-t border-brand-border space-y-3">
                <div className="text-[10px] font-mono tracking-widest text-brand-primary uppercase">/ EVENT MODE (optional)</div>
                <EditorField k="event_title" label={t.dashboard.profile.eventTitle} p={p} update={update} />
                <EditorField k="website_url" label={t.dashboard.profile.websiteUrl} type="url" p={p} update={update} />
            </div>
            <label className="block">
                <span className="text-[10px] font-mono tracking-widest text-brand-textMuted uppercase">Display language</span>
                <select value={p.language || "ro"} onChange={update("language")} className="mt-1 w-full rounded-xl border border-brand-border bg-brand-bg px-3 py-2 text-sm" data-testid={`profile-language`}>
                    <option value="ro">Română</option>
                    <option value="en">English</option>
                </select>
            </label>
            <button disabled={busy} onClick={save} className="w-full py-3 rounded-full btn-primary font-semibold text-sm disabled:opacity-60" data-testid={`save-${b.code}`}>
                {busy ? "..." : t.dashboard.saveProfile}
            </button>
        </div>
    );
}

function EditorField({ k, label, textarea, type = "text", p, update }) {
    const onChange = update(k);
    return (
        <label className="block">
            <span className="text-[10px] font-mono tracking-widest text-brand-textMuted uppercase">{label}</span>
            {textarea ? (
                <textarea
                    value={p[k] || ""}
                    onChange={onChange}
                    rows={2}
                    className="mt-1 w-full rounded-xl border border-brand-border bg-brand-bg px-3 py-2 text-sm focus:outline-none focus:border-brand-text"
                    data-testid={`profile-${k}`}
                />
            ) : (
                <input
                    type={type}
                    value={p[k] || ""}
                    onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-brand-border bg-brand-bg px-3 py-2 text-sm focus:outline-none focus:border-brand-text"
                    data-testid={`profile-${k}`}
                />
            )}
        </label>
    );
}
